// lib
import React, { useContext, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'

import ADND from '@aytm/lib/adnd/ADND'
import { DataContext } from '@aytm/app/stats/page/question-views/components/mapping/Component'

import { generateUID, plural } from '@aytm/app/stats/lib/helpers'

// components
import Variable from '@aytm/app/stats/page/question-views/components/mapping/Variable'

// styles
import style from '@aytm/app/stats/page/question-views/components/mapping/styles/Component.module.scss'

const onDNDPositionChange = ({ newPosition, dragItemUpdater } = {}) =>
  dragItemUpdater(node => {
    const itemPositionNode = node.querySelector(`.stats-Concept-Item-index`)
    if (null !== itemPositionNode) {
      itemPositionNode.innerHTML = null === newPosition ? '' : `${newPosition + 1}.`
    }
  })

const getDefaultVariable = (item = {}) => {
  return {
    key: generateUID(),
    text: '',
    pattern: '',
    rules: [],
    ...item
  }
}

const Variables = ({ items, note }) => {
  const { id, data, disabled, dispatch } = useContext(DataContext)

  const dndItemsListRef = useRef()

  const onDNDUpdateItemPositions = ({ hiddenItemIndex, placeholderIndex } = {}) => {
    const itemPositionNodes = dndItemsListRef.current.querySelectorAll(`.stats-Concept-Item .stats-Concept-Item-index`)

    for (const [i, itemPositionNode] of itemPositionNodes.entries()) {
      if (null !== itemPositionNode) {
        const visualItemIndex = i + (null !== hiddenItemIndex && hiddenItemIndex <= i ? -1 : 0)
        const newItemIndex =
          visualItemIndex + (null !== placeholderIndex && placeholderIndex <= visualItemIndex ? 1 : 0)
        itemPositionNode.innerHTML = null === newItemIndex ? '' : `${newItemIndex + 1}.`
      }
    }
  }

  const onDNDDrop = res => {
    const { item, position, clone } = res

    if (clone) {
      dispatch({
        type: 'ADD_VARIABLE',
        data: {
          item: {
            ...getDefaultVariable(item.item),
            key: generateUID()
          },
          position
        }
      })
    } else if (item.id != id) {
      item.remove()
      dispatch({
        type: 'ADD_VARIABLE',
        data: {
          item: {
            ...getDefaultVariable(item.item),
            key: generateUID()
          },
          position
        }
      })
    } else if (item.position != position) {
      dispatch({
        type: 'REORDER_VARIABLES',
        data: {
          from: item.position,
          to: position
        }
      })
    }

    onDNDUpdateItemPositions()
  }

  const dndItemsList = ADND.targets
    .itemsList(
      {
        acceptTypes: [`dlq-variable-${data.windowUID}`],
        placeholderVMargin: 10
      },
      { capture: false }
    )
    .buildPlaceholder(() => {
      const placeholderNode = document.createElement('div')
      placeholderNode.className = `${style.placeholder}`
      return placeholderNode
    })
    .onPositionChange(onDNDPositionChange)
    .onUpdateItemPositions(onDNDUpdateItemPositions)
    .onEnter(onDNDUpdateItemPositions)
    .onLeave(onDNDUpdateItemPositions)
    .onDrop(onDNDDrop)

  const onAddClick = () => {
    dispatch({
      type: 'ADD_VARIABLE',
      data: {
        item: getDefaultVariable(),
        position: items.length
      }
    })
  }

  useEffect(() => {
    dndItemsList.connect(dndItemsListRef.current)

    return () => {
      dndItemsList.disconnect()
    }
  }, [dndItemsList])

  return (
    <div className={style.component}>
      <div className={`${style.row} ${style['row-header']} p-fluid p-col-12`}>
        <b className={style.title}>
          {items.length} {plural(items.length, 'variable', 'variables')} {note}
        </b>
      </div>
      <div
        className={`${style.row} ${style['row-note']}${
          disabled ? ' ' + style['row-note-disabled'] : ''
        } p-fluid p-col-12`}
      ></div>
      <div ref={dndItemsListRef} className={style.variables}>
        {items.map((item, index) => (
          <Variable key={item.key} item={item} position={index + 1} dndItemsList={dndItemsList} />
        ))}
      </div>
      {disabled ? null : (
        <div className={`${style.row} ${style['row-add']} p-fluid p-col-12`}>
          <div className={`${style.index} p-field`}>{items.length + 1}.</div>
          <div className="p-field">
            <span className={style.add} onClick={onAddClick}>
              Add a variable
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

Variables.propTypes = {
  items: PropTypes.array,
  note: PropTypes.string
}

Variables.displayName = 'Variables'

export default Variables

ADND().connect()
